'use strict';

import { pageRowCount } from './paging';

/**
 * Validation rules — "has this question been answered enough to proceed?"
 *
 * Data-only ports of two legacy jQuery helpers that decided the same question
 * from the rendered page:
 *
 *   - `helpers.js` `isRequiredSatisfied` — every visible row answered (grid, and
 *     the Base default).
 *   - `single-multiple/displaymax.js` `requiredIsSatisfied` — on the last page,
 *     at least one option chosen (SingleMultiple).
 *
 * Both counted rows off the DOM via `base/displaymax.js` `getSelectedRowsCount`.
 * These rules reproduce that count from the question object and an answers
 * snapshot, so validation survives a rendering change and never disagrees with
 * submission, which already reads the store (RPI-880). See `../answers-adapter`
 * for how a widget builds the snapshot and `./README.md` for the contract.
 */

/**
 * Is a stored value a selection? Presence, not identity: neither `undefined`
 * (unwritten) nor `''` (written while empty). The value is opaque — a checked
 * box stores `'on'`, a radio/dropdown its code, an open answer its text — so it
 * is never compared to a response code. See `on-vs-code` in `../answers-adapter`.
 */
function isSelected(answers, name) {
  if (typeof name !== 'string') {
    return false;
  }
  var value = answers[name];
  return value !== undefined && value !== '';
}

/**
 * The rows shown on the current page. `question.phases` is the `displaymax`
 * chunking the backend sends (grid rows / SingleMultiple options), and
 * `phases[phase_pos]` is the current chunk — what `visible_rows_count`
 * measured. An out-of-range `phase_pos` reads as no rows (an empty
 * `#phase-{pos}`) rather than falling back to every row. A non-paginated
 * question carries no `phases`, so fall back to whichever row container it has.
 * (The at-least-one rule counts the whole question instead; see `allRows`.)
 */
function currentPhaseRows(question) {
  var phases = question.phases;
  if (Array.isArray(phases) && phases.length) {
    var pos = question.phase_pos || 0;
    return Array.isArray(phases[pos]) ? phases[pos] : [];
  }
  if (Array.isArray(question.category_options)) {
    return question.category_options;
  }
  if (Array.isArray(question.response_options)) {
    return question.response_options;
  }
  return [];
}

/**
 * Every row in the question, across all pages. The at-least-one rule counts a
 * choice made anywhere, matching the legacy `getSelectedRowsCount`, which walked
 * every rendered row (hidden pages included). `response_options`/
 * `category_options` hold the full list; fall back to flattening `phases`.
 */
function allRows(question) {
  if (Array.isArray(question.category_options)) {
    return question.category_options;
  }
  if (Array.isArray(question.response_options)) {
    return question.response_options;
  }
  var phases = question.phases;
  if (Array.isArray(phases)) {
    return phases.reduce(function (all, chunk) {
      return Array.isArray(chunk) ? all.concat(chunk) : all;
    }, []);
  }
  return [];
}

/**
 * The answer variables a single row owns. Mirrors `../answers-adapter`
 * `variables.js`: a grid row names its cells in `response_inputs`, a checkbox
 * option names itself in `input_name`. A row with neither belongs to a
 * shared-variable question (radio/dropdown, one `question.input_id`) and is
 * reported as `null` so the caller counts that variable once, not per option.
 */
function rowVariables(question, row) {
  if (row && Array.isArray(row.response_inputs)) {
    return row.response_inputs;
  }
  if (row && typeof row.input_name === 'string') {
    return [row.input_name];
  }
  return null;
}

/**
 * Count answered rows in `rows`. The data-only port of `getSelectedRowsCount`
 * and the shared body of `selectedCount` (current page) and `selectedInQuestion`
 * (whole question). A row counts when, for a text grid (`input_type === 'text'`),
 * every input holds a value, otherwise any input does. A shared-variable
 * question declares no per-row variable, so its `question.input_id` is counted
 * once. Selection is presence only (see `isSelected`).
 */
function countSelected(question, answers, rows) {
  var isText = question.input_type === 'text';
  var perRow = rows.some(function (row) {
    return rowVariables(question, row) !== null;
  });

  if (!perRow) {
    // Radio or dropdown: one shared variable for the whole question.
    return isSelected(answers, question.input_id) ? 1 : 0;
  }

  var count = 0;
  for (var i = 0; i < rows.length; i++) {
    var names = rowVariables(question, rows[i]);
    if (!names || names.length === 0) {
      continue;
    }
    var answered = isText
      ? names.every(function (name) {
          return isSelected(answers, name);
        })
      : names.some(function (name) {
          return isSelected(answers, name);
        });
    if (answered) {
      count++;
    }
  }
  return count;
}

/**
 * Answered count for a paged Grid/Matrix page, matching the legacy DOM count
 * including its quirks: `number` cells always count (it only saw
 * `input:text`), and the transposed layout counts cells, not rows.
 *
 * @param {Object} question The question object, read-only.
 * @param {Object} answers Snapshot keyed by variable name, read-only.
 * @param {boolean} transposed Rows are drawn as columns.
 * @returns {number} The legacy answered count.
 */
function displaymaxSelectedCount(question, answers, transposed) {
  var rows = currentPhaseRows(question);
  var isNumber = question.input_type === 'number';

  if (!transposed) {
    return isNumber ? rows.length : countSelected(question, answers, rows);
  }

  var perRow = rows.some(function (row) {
    return rowVariables(question, row) !== null;
  });
  if (!perRow) {
    return countSelected(question, answers, rows);
  }

  var count = 0;
  for (var i = 0; i < rows.length; i++) {
    var names = rowVariables(question, rows[i]) || [];
    if (isNumber) {
      count += names.length;
      continue;
    }
    // Distinct names: a radio row repeats one variable across its cells.
    var seen = Object.create(null);
    for (var j = 0; j < names.length; j++) {
      if (!seen[names[j]] && isSelected(answers, names[j])) {
        seen[names[j]] = true;
        count++;
      }
    }
  }
  return count;
}

/**
 * Does the category at `index` hold at least one code (Colorpicker)?
 */
function categoryHasCode(question, answers, index) {
  var categories = Array.isArray(question.category_options)
    ? question.category_options
    : [];
  var category = categories[index];
  return !!category && isSelected(answers, category.name);
}

/**
 * How many rows on the current page count as answered.
 *
 * @param {Object} question The question object, read-only.
 * @param {Object} answers Snapshot keyed by variable name, read-only.
 * @returns {number} Count of answered rows on the current page.
 */
export function selectedCount(question, answers) {
  if (!question || typeof question !== 'object') {
    return 0;
  }
  return countSelected(question, answers || {}, currentPhaseRows(question));
}

/**
 * How many answered rows the whole question holds, across every page. Used by
 * the at-least-one rule; see `allRows`.
 */
function selectedInQuestion(question, answers) {
  if (!question || typeof question !== 'object') {
    return 0;
  }
  return countSelected(question, answers || {}, allRows(question));
}

/**
 * How many rows are shown on the current page. The data-only port of
 * `visible_rows_count`, for the transposed displaymax layout too.
 *
 * @param {Object} question The question object, read-only.
 * @returns {number} Count of rows on the current page.
 */
export function visibleRowCount(question) {
  if (!question || typeof question !== 'object') {
    return 0;
  }
  // Shape the question into the page's row count, then let the paging layer own
  // the arithmetic — the standard, non-transposed case here. See
  // `./paging.js` `pageRowCount`.
  return pageRowCount(currentPhaseRows(question).length);
}

/**
 * Is the question's required rule satisfied on the current page? Ports the
 * legacy variants behind named options:
 *
 *   - default (grid, Base): `selectedCount >= visibleRowCount` — every visible
 *     row answered.
 *   - `opts.phaseValue` (Thermometer): one integer per phase. The current
 *     phase passes if its value parses as an integer (see `phaseValue`) or its
 *     own "don't know" box, `answers[question.phase_dks[question.phase]]`, is
 *     selected. The data-only port of the required check in Thermometer's
 *     `next_thermometer` (RPI-1184). Takes precedence over `atLeastOne`.
 *   - `opts.atLeastOne` (SingleMultiple): checked only on the last page, against
 *     "at least one row answered anywhere in the question" — the count spans
 *     every page, matching the legacy helper; earlier pages always pass so
 *     pagination continues.
 *
 *   - `opts.displaymax` (paged Grid/Matrix, `{ transposed, noTable }`): the
 *     default variant with the legacy count (see `displaymaxSelectedCount`).
 *
 *   - `opts.categoryIndex` (Colorpicker): at least one code in that category.
 *
 * The gate is shared: a HARD question is always checked, a soft-required one
 * until its prompt has shown once (`opts.softPromptsShown`), anything else
 * passes. "Soft-required" is `soft_required`, except under `opts.phaseValue`,
 * where it is `required === 'SOFT'`: the backend serializes only `required`
 * for Thermometer (`thermometer.py`), so a `soft_required` gate would never
 * check. `shouldProceed` in `./conditionals.js` reads it the same way.
 * `opts.softRequired` overrides both for callers whose payload lacks it.
 * `opts.dontKnow` (a checked DK control) always passes.
 *
 * Counts come from `selectedCount`/`visibleRowCount`, or a caller that still
 * measures off the DOM (Cardsort) injects
 * `opts.selectedCount`/`opts.visibleCount`.
 *
 * @param {Object} question Supplies `required`, `soft_required`, (for
 *     `atLeastOne`) `phase_pos`/`phase_max`, (for `phaseValue`) `phase`,
 *     `phase_inputs`/`phase_dks`, and (for `categoryIndex`) `category_options`.
 * @param {Object} answers Snapshot keyed by variable name.
 * @param {Object} [opts] `phaseValue`, `atLeastOne`, `displaymax`,
 *     `categoryIndex`, `softRequired`, `softPromptsShown`, `dontKnow`,
 *     `selectedCount`, `visibleCount`.
 * @returns {boolean} `true` when the respondent may proceed.
 */
export function isRequiredSatisfied(question, answers, opts) {
  opts = opts || {};
  question = question || {};

  var soft = opts.phaseValue
    ? question.required === 'SOFT'
    : question.soft_required;
  if (typeof opts.softRequired === 'boolean') {
    soft = opts.softRequired;
  }
  var mustCheck =
    question.required === 'HARD' || (soft && !opts.softPromptsShown);
  var injected = typeof opts.selectedCount === 'number';

  if (opts.dontKnow) {
    return true;
  }

  if (opts.phaseValue) {
    if (!mustCheck) {
      return true;
    }
    answers = answers || {};
    if (!isNaN(phaseValue(question, answers))) {
      return true;
    }
    var dks = question.phase_dks;
    return isSelected(answers, Array.isArray(dks) ? dks[question.phase] : null);
  }

  if (opts.atLeastOne) {
    // Earlier pages always pass; the check waits for the last page. Mirror the
    // legacy `phase_pos + 1 > phase_max` test, including its behavior when the
    // phase fields are absent (`NaN > undefined` is false, so not-last, passes).
    if (!(question.phase_pos + 1 > question.phase_max)) {
      return true;
    }
    if (!mustCheck) {
      return true;
    }
    // "At least one anywhere in the question": count across every page.
    var chosen = injected
      ? opts.selectedCount
      : selectedInQuestion(question, answers);
    return chosen >= 1;
  }

  if (!mustCheck) {
    return true;
  }
  if (typeof opts.categoryIndex === 'number') {
    return categoryHasCode(question, answers || {}, opts.categoryIndex);
  }
  if (opts.displaymax && !injected) {
    var transposed = !!opts.displaymax.transposed;
    var responseRows = Array.isArray(question.response_options)
      ? question.response_options.length
      : 0;
    // No transposed table: legacy compared against 0 / 0 = NaN, never satisfied.
    if (transposed && (opts.displaymax.noTable || responseRows === 0)) {
      return false;
    }
    return (
      displaymaxSelectedCount(question, answers || {}, transposed) >=
      visibleRowCount(question)
    );
  }
  var selected = injected
    ? opts.selectedCount
    : selectedCount(question, answers);
  var visible =
    typeof opts.visibleCount === 'number'
      ? opts.visibleCount
      : visibleRowCount(question);
  return selected >= visible;
}

/**
 * A question that shows one row per phase (Dyngrid: one category per screen),
 * reshaped into the paged form the rules above already read: every row its own
 * `phases` chunk, and `phase` as `phase_pos`. The rules then see only that
 * row, rather than falling back to every row in `category_options`.
 *
 * Also fills `soft_required` from `required === 'SOFT'` when the question does
 * not carry it. The backend sends only `required` for such widgets (Dyngrid's
 * serializer), and without the flag the soft gate in `isRequiredSatisfied`
 * would never check. A `soft_required` the question does carry is kept.
 *
 * Returns a shallow copy; the question is not modified.
 *
 * @param {Object} question The question object, read-only.
 * @param {number} phase Index of the row to show.
 * @returns {Object} The paged view of the question.
 */
export function rowPerPhaseView(question, phase) {
  question = question || {};
  var rows = allRows(question);
  var view = Object.assign({}, question, {
    phases: rows.map(function (row) {
      return [row];
    }),
    phase_pos: phase,
  });
  if (view.soft_required === undefined) {
    view.soft_required = question.required === 'SOFT';
  }
  return view;
}

/**
 * Does the given phase of a one-row-per-phase question hold any selection?
 * Whether a phase is complete, without regard to `required`. "Don't know" is
 * not counted; it is not a variable of the row.
 *
 * @param {Object} question The question object, read-only.
 * @param {Object} answers Snapshot keyed by variable name, read-only.
 * @param {number} phase Index of the phase.
 * @returns {boolean} `true` when at least one input in the phase is answered.
 */
export function isPhaseAnswered(question, answers, phase) {
  return selectedCount(rowPerPhaseView(question, phase), answers) > 0;
}

/**
 * The current phase's value, for a question that collects one integer per
 * phase (Thermometer: one statement per screen). Reads
 * `answers[question.phase_inputs[question.phase]]` and parses it with
 * `parseInt`, as the legacy `next_thermometer` did, so `'12abc'` reads as `12`
 * and an empty or unwritten value reads as `NaN`. No radix, also as the legacy
 * check did.
 */
function phaseValue(question, answers) {
  var inputs = question.phase_inputs;
  var name = Array.isArray(inputs) ? inputs[question.phase] : undefined;
  return parseInt(typeof name === 'string' ? answers[name] : undefined);
}

/**
 * Is the current phase's value inside the question's range? The data-only port
 * of the range check in Thermometer's `next_thermometer`. The bounds are
 * `therm_min` and `therm_max`, inclusive; no other widget sends a range yet.
 *
 * An unanswered value (`NaN`, see `phaseValue`) passes: whether the phase may
 * be left empty is `isRequiredSatisfied`'s decision, under `opts.phaseValue`.
 *
 * @param {Object} question Supplies `phase`, `phase_inputs`, `therm_min` and
 *     `therm_max`. Read-only.
 * @param {Object} answers Snapshot keyed by variable name, read-only.
 * @returns {boolean} `false` only when the value lies outside the range.
 */
export function isPhaseValueInRange(question, answers) {
  question = question || {};
  var value = phaseValue(question, answers || {});
  return !(value > question.therm_max || value < question.therm_min);
}
